import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const api = axios.create({
  baseURL: API_URL,
  headers: { 'Content-Type': 'application/json' }
});

// Global toast handler — registered by ToastProvider on mount.
// Falls back to console if not yet registered.
let _toastHandler = null;
export function registerApiToastHandler(handler) {
  _toastHandler = handler;
}
function emitToast(type, message) {
  if (_toastHandler && typeof _toastHandler[type] === 'function') {
    _toastHandler[type](message);
  } else {
    // eslint-disable-next-line no-console
    console.warn(`[api/${type}]`, message);
  }
}

// Attach token from localStorage to every request
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('access_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Suppress duplicate error toasts within a 4s window
const _recentErrorMessages = new Map();
function shouldSuppress(key) {
  const now = Date.now();
  const last = _recentErrorMessages.get(key);
  if (last && now - last < 4000) return true;
  _recentErrorMessages.set(key, now);
  // GC old entries occasionally
  if (_recentErrorMessages.size > 50) {
    for (const [k, t] of _recentErrorMessages.entries()) {
      if (now - t > 10000) _recentErrorMessages.delete(k);
    }
  }
  return false;
}

// Handle errors uniformly: 401 refresh-or-logout, 429 toast, 500/network friendly toast.
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config || {};
    const url = originalRequest.url || '';
    const isAuthEndpoint = url.includes('/auth/login') || url.includes('/auth/refresh');
    const status = error.response?.status;

    // 401 — try to refresh once, otherwise logout
    if (status === 401 && !originalRequest._retry && !isAuthEndpoint) {
      originalRequest._retry = true;
      try {
        const refreshToken = localStorage.getItem('refresh_token');
        const { data } = await api.post('/api/auth/refresh', { refresh_token: refreshToken });
        if (data.access_token) {
          localStorage.setItem('access_token', data.access_token);
        }
        return api(originalRequest);
      } catch (refreshError) {
        localStorage.removeItem('access_token');
        localStorage.removeItem('refresh_token');
        // Avoid redirect loop if already on login
        if (!window.location.pathname.startsWith('/login')) {
          window.location.href = '/login';
        }
        return Promise.reject(refreshError);
      }
    }

    // Skip toast for explicit silentError config
    if (originalRequest.silentError) {
      return Promise.reject(error);
    }

    // 429 — rate limit
    if (status === 429) {
      const retryAfter = error.response?.headers?.['retry-after'];
      const msg = retryAfter
        ? `Du sender for mange forespørsler. Prøv igjen om ${retryAfter}s.`
        : 'Du sender for mange forespørsler. Vent litt og prøv igjen.';
      if (!shouldSuppress(`429:${url}`)) emitToast('warning', msg);
    }
    // 500-range — friendly server error
    else if (status >= 500 && status < 600) {
      if (!shouldSuppress(`5xx:${url}`)) emitToast('error', 'Serverfeil — vi jobber med saken. Prøv igjen om litt.');
    }
    // Network / timeout
    else if (!error.response && error.code !== 'ERR_CANCELED' && !isAuthEndpoint) {
      if (!shouldSuppress(`net:${url}`)) emitToast('error', 'Kunne ikke nå serveren. Sjekk internettforbindelsen.');
    }

    return Promise.reject(error);
  }
);

export default api;

export const getCallSuggestions = (callId) => api.get(`/api/calls/${callId}/suggestions`);
export const updateCallNotes = (callId, notes) => api.patch(`/api/calls/${callId}/notes`, { notes });
export const updateSuggestion = (suggestionId, status) => api.patch(`/api/suggestions/${suggestionId}`, { status });
export const getPendingSuggestionsCount = (companyId) => api.get(`/api/companies/${companyId}/suggestions/pending-count`);
export const getROIData = (companyId) => api.get(`/api/companies/${companyId}/roi`);

export function formatApiError(detail) {
  if (detail == null) return 'Noe gikk galt. Prov igjen.';
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail))
    return detail.map(e => (e && typeof e.msg === 'string' ? e.msg : JSON.stringify(e))).filter(Boolean).join(' ');
  if (detail && typeof detail.msg === 'string') return detail.msg;
  return String(detail);
}
